import axios, { AxiosError } from 'axios'
import type { ApiEnvelope } from '@/types'

const baseURL = import.meta.env.VITE_API_BASE_PATH || '/api'

export const http = axios.create({
  baseURL,
  withCredentials: true, // PHP sessions live in cookies
  timeout: 20000,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

export interface ApiCallOptions {
  silent?: boolean
}

export class ApiError extends Error {
  status: number
  errors?: Record<string, string>
  constructor(message: string, status: number, errors?: Record<string, string>) {
    super(message)
    this.status = status
    this.errors = errors
  }
}

/** Unwrap our standard envelope or throw `ApiError`. */
export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  return unwrap<T>(() => http.get<ApiEnvelope<T>>(url, { params }))
}

export async function apiPost<T>(url: string, body?: Record<string, unknown> | FormData): Promise<T> {
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData
  return unwrap<T>(() =>
    http.post<ApiEnvelope<T>>(url, body, {
      headers: isForm ? { 'Content-Type': 'multipart/form-data' } : undefined,
    }),
  )
}

async function unwrap<T>(req: () => Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  try {
    const { data } = await req()
    if (data && data.ok === false) {
      throw new ApiError(data.message || 'Request failed', 400, data.errors)
    }
    // If the envelope shape is present, return its `data` (which may legally
    // be null — e.g. /auth/me.php when no session). Only fall through to the
    // raw body for endpoints that don't follow the envelope convention.
    if (data && typeof data === 'object' && 'ok' in data) {
      return data.data as T
    }
    return data as unknown as T
  } catch (err) {
    if (err instanceof ApiError) throw err
    const axErr = err as AxiosError<ApiEnvelope<unknown>>
    const status = axErr.response?.status ?? 0
    const payload = axErr.response?.data
    const message =
      payload?.message ||
      (status === 401
        ? 'Your session has expired, please sign in again.'
        : status === 0
          ? 'Network error - please check your connection.'
          : 'Something went wrong, please try again.')
    throw new ApiError(message, status, payload?.errors)
  }
}
